import React, { useState, useMemo, useCallback, ReactNode } from 'react';
import { styled } from '@mui/material';

import Toolbar from '@/components/Toolbar';
import FilterTabsBar from '@/components/FilterTabsBar';
import FilterTabButton from '@/components/FilterTabButton';
import SearchInput from '@/components/SearchInput';
import TableList, { ColumnMeta, ColumnWidthType } from '@/components/TableList';
import { PageSection } from '../common.controls';
import { autocompleteClasses } from '@mui/material/Autocomplete';

import { RootState, useAppSelector } from '@/state/store';
import { createSelector } from '@reduxjs/toolkit';
import { LinearProgress } from '@mui/material';
import DateCard from '@/components/DateCard';
import { avatar } from '@/loaders/user';
import { ModelData } from '@/state/types/model-data';
import { Profile, User } from '@bimdata/definitions/dto';
import { ProjectData } from '@/state/types/project-data';
import UserCard from '@/components/UserCard';

import { update } from '@/actions/project';
import {
  remove as modelsRemove,
  edit as modelEdit,
  bulkSetSection
} from '@/actions/model';

import MarkerIcon from '@/components/icons/Marker';
import FailedIcon from '@/components/icons/Failed';
import PendingIcon from '@/components/icons/Pending';
import InfoIcon from '@/components/icons/Info2';

import Memory from '@/components/Memory';
import hinted from '@/components/Hinted';
import Highlighter from '@/components/Highlighter';
import ModelDownloadWidget from './ModelDownloadWidget';
import { BulkAbortDownloadingButton, BulkDeleteButton, BulkDownloadButton, DeleteButton } from './buttons';
import SectionField from '@/pages/project/SectionField';
import t from '@/utils/translate';
import { saveLastProjectUpdateModelsSorting } from '@/actions/client';
import { ModelsSorting, ModelsSortingBy } from '@/state/authentication.slice';
import Prompt from '@/components/Prompt';
import { BulkReprocessButton, ReprocessButton } from './buttons';
import SectionSelect, { SectionSelectProps } from '../SectionSelect';
import { escRegExpSpecChars } from '@/utils/regexp';
import { fontWeight, height, padding, width } from '@mui/system';

const RevitVersionCard = styled('span')`
  background-color: #EBECF0;
  padding: 4px 6px;
  display: inline-block;
  border-radius: 5px;
  font-size: 13px;
`;

export type ProjectModelsProps = {
  currentUser: Profile & User;
  project: ProjectData;
  placeholder?: ReactNode;
};

const StyledUserCard = styled(UserCard)(({ hideEmail }) => ({
  ['& div.MuiAvatar-root']: {
    width: '32px',
    height: '32px',
  },
  ['& span.UserCard-fullname']: {
    fontWeight: hideEmail ? 700 : 400,
  }
}));

const ConfirmDeletionList = styled('ul')({
  margin: 0,
  listStyle: 'none',
  background: '#EBECF0',
  gap: '1px',
  padding: '1px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',

  '&>li': {
    background: '#FFFFFF',
    height: '27px',
    width: '100%',
    padding: '6px 10px',
    fontSize: '13px',
    color: '#091E42',
    fontWeight: '700'
  }
});

const Succeed = styled(MarkerIcon)({
  color: '#00875A',
  fontSize: '20px',
});

const Status = hinted(
  styled('div')`
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  `,
  {}
);

const ModelStatus = ({ item }: { item: ModelData }) => {
  if (item.failed) {
    return (<Status hint={t('model.state.importFailed')}><Failed /></Status>);
  } else if (!item.ready) {
    return (<Status hint={t('model.state.import')}><Pending /></Status>);
  } else if (item.elements === 0) {
    return (<Status hint={t('model.state.empty')}><Info /></Status>);
  }
  return (<Succeed />);
}

const NameField = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  overflow: 'hidden'
});

const StatusBox = styled('div')({
  flexShrink: 0,
  padding: '2px',
  display: 'flex',
  alignItems: 'center'
});
const NameBox = styled('span')({
  overflowX: 'clip',
  textOverflow: 'ellipsis',
  flex: '1 1 auto',
  minWidth: 0,
});

const Failed = styled(FailedIcon)({
  color: '#DE350B',
  width: '20px',
  height: '20px',
});

const Pending = styled(PendingIcon)({
  color: '#00B8D9',
  //opacity: 0.5,
  width: '20px',
  height: '20px',
});

const Info = styled(InfoIcon)({
  color: '#6B778C',
  width: '20px',
  height: '20px',
});

type StateFilter = 'all' | 'ready' | 'problem' | 'processing';

type Section = {
  code: string;
  title: string;
  data: ModelData[];
}

function attrValue(item: ModelData, nm: ModelsSortingBy) {
  switch (nm) {
    case 'name': return item.name;
    case 'author': return item.author.fullName;
    case 'loaded': return item.loaded;
    case 'revitVersion': return item.revitVersion || '';
    case 'sourceSize': return item.sourceSize || 0;
  }
}

const idGetter = (item: ModelData) => item.id;

type AuthorCardProps = {
  item: ModelData;
  currentUser: User;
}

const AuthorCard = ({ item, currentUser }: AuthorCardProps) => {
  const user = item.author;
  const { avatar } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.user.avatars[user.id]?.source,
      ],
      (avatar) => ({ avatar })
    )
  );

  return (
    <StyledUserCard
      profile={user.id === currentUser.id ? currentUser : user}
      hideEmail={user.id === currentUser.id ? true : undefined}
      avatar={(!!user.avatar && avatar) || false}
    />
  );
}

type BulkSectionFieldProps = {
  project: ProjectData,
  items: Array<ModelData>
} & Omit<SectionSelectProps, 'onChange' | 'placeholder'>;4

const BulkSectionFieldView = styled(SectionSelect)({
  [`& .${autocompleteClasses.inputRoot}`]: {
    padding: '5px 4px 5px 10px',
    width: '100px',
    height: '30px',
    background: '#FFFFFF',
    border: '1px solid #DFE1E6',
    borderRadius: '4px',

    '& fieldset': {
      border: 'none',
    },

    '&:hover fieldset': {
      border: 'none',
    },

    '&.Mui-focused fieldset': {
      border: 'none',
    },

    '&.Mui-disabled': {
      filter: 'saturate(0) opacity(0.85)',
      cursor: 'default',
    },
  },

  [`& .${autocompleteClasses.input}`]: {
    color: '#6B778C',
    fontWeight: 400,
    fontSize: '13px',
    padding: 0,
  },

  [`& .${autocompleteClasses.popupIndicator}`]: {
    color: '#C1C7D0',
    padding: 0,
    marginRight: 4,
  }
});

const BulkSectionField = ({ items, project, ...props }: BulkSectionFieldProps) => {
  const onChangeHandler = useCallback(
    async (s: string) => bulkSetSection(project.code, Array.from(items, (i) => i.id), s),
    [ project, items ]
  );

  return <BulkSectionFieldView
    {...props}
    onChange={onChangeHandler}
    value="Раздел"
  />;
}

const defaultSorting = ['name', 'A'] as ModelsSorting;

export default ({ currentUser, project, placeholder }: ProjectModelsProps) => {
  const { avatars, sorting } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.user.avatars,
        (state: RootState) => state.authentication.clientState.lastProjectUpdateModelsSorting || defaultSorting,
      ],
      (avatars, sorting) => ({ avatars, sorting })
    )
  );

  const sections = project.models;

  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [search, setSearch] = useState<string>('');
  const [removingModels, setRemovingModels] = useState<ModelData[]>([]);
  const [modelRemoveWaiting, setModelRemoveWaiting] = useState(false);
  const [selected, setSelected] = useState<ModelData[]>([]);

  const startDeletion = useCallback((models: ModelData[]) => { setRemovingModels(models); }, []);

  const deleteModel = useCallback(async () => {
    if (project && removingModels.length) {
      setModelRemoveWaiting(true);
      await modelsRemove(project.code, removingModels.map((m) => m.id));
      setModelRemoveWaiting(false);
    }
    setRemovingModels([]);
  }, [project, removingModels ]);

  const onSearchChange = useCallback((v: string) => {
    setSearch(v);
  }, []);

  const sortingHandler = useCallback((by: ModelsSortingBy, order: 'A' | 'D') => {
    saveLastProjectUpdateModelsSorting([ by, order ]);
  }, []);

  const ensureRow = useCallback((item: ModelData) => {
    const user = item.author;
    if (user.avatar && (avatars[user.id]?.source || null) === null) {
      avatar(user);
    }
  }, [avatars]);

  const createCustomSection = useCallback((s: string) => (
    update(
      project.code,
      { additionalSections: [ s, ...project.additionalSections ] }
    )
  ), [ project ]);

  const columns = useMemo<ColumnMeta[]>(() => {
    const sre = search ? new RegExp(escRegExpSpecChars(search), 'i') : null;
    return [
    {
      attrName: 'name',
      title: t('model.column.name'),
      hinted: true,
      dataGetter: (item: ModelData) => {
        return <NameField>
          <StatusBox><ModelStatus item={item} /></StatusBox>
          <NameBox>{(sre ? <Highlighter pattern={sre}>{item.name}</Highlighter> : item.name)}</NameBox>
        </NameField>
      },
    },
    {
      attrName: 'author',
      title: t('model.column.author'),
      dataGetter: (item: ModelData) => <AuthorCard item={item} currentUser={currentUser} />,
    },
    {
      attrName: 'loaded',
      title: t('model.column.uploaded'),
      dataGetter: (item: ModelData) => <DateCard value={item.loaded} />,
      defaultSorting: 'D',
      width: 130,
      style: { textAlign: 'right' }
    },
    {
      attrName: 'section',
      title: t('model.column.section'),
      noSort: true,
      width: 120,
      dataGetter: (item: ModelData) => (
        <SectionField
          additionalSections={project.additionalSections}
          value={item.section}
          onChange={(s: string) => modelEdit(project.code, item.id, { section: s })}
          onCustomSection={createCustomSection}
        />
      ),
    },
    {
      attrName: 'revitVersion',
      title: t('model.column.version'),
      width: 70,
      dataGetter: (item: ModelData) => {
        return item.revitVersion && <RevitVersionCard>{item.revitVersion}</RevitVersionCard>
      },
    },
    {
      attrName: 'sourceSize',
      title: t('model.column.size'),
      width: 80,
      dataGetter: (item: ModelData) => {
        return <Memory value={item.sourceSize || 0} />
      },
    },
    {
      width: ColumnWidthType.Hug,
      type: 'iconic',
      style: { textAlign: 'center' },
      dataGetter: (item: ModelData) => <ModelDownloadWidget project={project} item={item} />,
    },
    {
      width: ColumnWidthType.Hug,
      type: 'iconic',
      style: { textAlign: 'center' },
      dataGetter: (item: ModelData) => <DeleteButton project={project} item={item} onAction={startDeletion} />,
    },
    ...(currentUser.isSuperadmin ? [{
      width: ColumnWidthType.Hug,
      type: 'iconic',
      style: { textAlign: 'center' },
      dataGetter: (item: ModelData) => (<ReprocessButton project={project} items={item}/>
      ),
    } as ColumnMeta] : [])
  ];}, [search, project, currentUser]);

  const sorter = (a: ModelData, b: ModelData) => {
    const av = (sorting[1] === 'A') ? attrValue(a, sorting[0]) : attrValue(b, sorting[0]);
    const bv = (sorting[1] === 'A') ? attrValue(b, sorting[0]) : attrValue(a, sorting[0]);
    if (typeof av === 'string' || typeof bv === 'string') {
      return `${av}`.localeCompare(`${bv}`);
    }
    return +av - +bv;
  };

  const checkSearchFilter = (m: ModelData): boolean => {
    if (!search) return true;
    return m.name.toLowerCase().includes(search.toLowerCase());
  }

  const stats = useMemo<{ all: number, ready: number, problem: number, processing: number }>(() => {
    const result = {
      all: 0,
      ready: 0,
      problem: 0,
      processing: 0,
    };

    if (!sections) return result;

    sections.forEach(({ models }) => {
      models.forEach((m) => {
        if (!checkSearchFilter(m)) return;
        result.all++;
        if (m.failed || (m.ready && !m.elements)) {
          result.problem++;
        } else if (!m.ready) {
          result.processing++;
        } else {
          result.ready++;
        }
      });
    });
    return result;
  }, [sections, search]);

  const data = useMemo<Section[]>(
    () => {
      if (!sections) return [];

      return sections.map(({ models, name }) => ({
        code: name || '',
        title: name || t('model.noSection'),
        data: models.filter((m) => {
          if (!checkSearchFilter(m)) return false;
          switch (stateFilter) {
            case 'ready': return m.ready && !m.failed && !!m.elements;
            case 'problem': return m.failed || (m.ready && !m.elements);
            case 'processing': return !m.ready && !m.failed;
          }
          return true;
        }).sort(sorter)
      }));
    },
    [sections, stateFilter, search, sorting]
  );

  return <PageSection style={{ height: '100%', width: '100%' }}>
    {(!sections) && <LinearProgress />}
    {sections && !(sections?.length || 0) && placeholder}
    {!!(sections?.length || 0) && (
      <>
        <Toolbar>
          <FilterTabsBar>
            {!!stats.all &&
              <FilterTabButton
                value={'all'}
                filter={stateFilter}
                onClick={setStateFilter}
                score={stats.all}
              >
                {t('model.list.all')}
              </FilterTabButton>
            }
            {!!stats.ready &&
              <FilterTabButton
                value={'ready'}
                filter={stateFilter}
                onClick={setStateFilter}
                score={stats.ready}
              >
                {t('model.list.success')}
              </FilterTabButton>
            }
            {!!stats.problem &&
              <FilterTabButton
                value={'problem'}
                filter={stateFilter}
                onClick={setStateFilter}
                score={stats.problem}
              >
                {t('model.list.problem')}
              </FilterTabButton>
            }
            {!!stats.processing &&
              <FilterTabButton
                value={'processing'}
                filter={stateFilter}
                onClick={setStateFilter}
                score={stats.processing}
              >
                {t('model.list.processed')}
              </FilterTabButton>
            }
          </FilterTabsBar>
          <SearchInput
            onChange={onSearchChange}
            value={search}
            placeholder={t('action.search')}
          />
        </Toolbar>
        <TableList<ModelData>
          sorting={sorting}
          columns={columns}
          data={data}
          grouped
          hideEmptyGroups
          onRowVisible={ensureRow}
          onSortingChange={sortingHandler}
          idGetter={idGetter}
          isCheckList={true}
          checkListChange={setSelected}
          selectionStatusCaption={t('model.list.selectionCaption')}
        >
          <BulkSectionField
            additionalSections={project.additionalSections}
            onCustomSection={createCustomSection}
            items={selected}
            project={project}
            disabled={!selected.length}
          />
          <BulkDownloadButton items={selected} project={project}/>
          <BulkAbortDownloadingButton items={selected} project={project}/>
          <BulkDeleteButton items={selected} project={project} onAction={startDeletion} />
          <BulkReprocessButton items={selected} project={project}/>
        </TableList>
      </>
    )
  }
    <Prompt
      open={!!removingModels.length}
      title={modelRemoveWaiting
        ? t('model.title.deletionInProgress', { count: removingModels.length })
        : t('model.title.confirmDeletion', { count: removingModels.length })
      }
      question={t('model.hint.confirmDeletion', { count: removingModels.length })}
      onClose='cancel'
      onEnter='confirm'
      actions={[
        {
          name: 'confirm',
          handler: deleteModel,
          caption: t('common.action.delete'),
          style: {
            border: '1px solid #DE350B',
            color: '#DE350B',
          }
        },
        {
          name: 'cancel',
          handler: () => setRemovingModels([]),
          caption: t('common.action.cancel'),
        }
      ]}
      disabled={modelRemoveWaiting}
    >
      <ConfirmDeletionList>
        {removingModels.map(({ name }) => <li key={name}>{name}</li>)}
      </ConfirmDeletionList>
    </Prompt>
    {/*<ConfirmationDialog
      title={modelRemoveWaiting ? t('model.title.DeletionInProgress') : t('model.title.ConfirmDeletion')}
      details={removingModel?.name}
      isOpen={!!removingModel}
      isBlocker={modelRemoveWaiting}
      onConfirm={deleteModel}
      onClose={() => setRemovingModel(null)}
    />*/}
  </PageSection>;
}
